// ABOUTME: The Jobs page's applications part: the skeleton while loading, the applications block with one row per
// ABOUTME: application, and the empty, off-for-this-talent and error states.
import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import { EmptyState } from 'components/EmptyState';
import { NotFoundErrorScreen } from 'components/screens';
import { sendAtsTrackerViewed } from 'modules/analytics';
import { getTrans } from 'modules/i18n';
import { useApplications } from '../hooks';
import { initialBlockOpen, movedToday, seenMoves, unseenMoved } from '../utils';
import { ApplicationsBlock } from './ApplicationsBlock';
import { ApplicationsSkeleton } from './ApplicationsSkeleton';

/**
 * @param variant the PRO_ATS arm the talent is in, reported with the view for the A/B readout.
 * @param now     the instant "today" is measured from; injectable so a test does not fake timers.
 */
export const Applications = ({
  variant,
  now = new Date()
}: {
  variant: string;
  now?: Date;
}) => {
  const { applications, isLoading, isError, isUnavailable } = useApplications();
  const reported = useRef(false);

  useEffect(() => {
    // One view per visit, empty lists included: "saw the tracker with nothing in it" is a data point too. Guarded by
    // a ref rather than by the list itself, because a refetch on focus or after a withdrawal resolves to a new array
    // and would otherwise report a second view the talent never made.
    if (applications && !reported.current) {
      reported.current = true;
      const movedTodayCount = applications.filter(application =>
        movedToday(application, now)
      ).length;
      sendAtsTrackerViewed({
        variant,
        applicationCount: applications.length,
        movedTodayCount,
        blockOpen: initialBlockOpen(
          unseenMoved(applications, now, seenMoves()).length
        )
      });
    }
  }, [applications, now, variant]);

  if (isLoading) return <ApplicationsSkeleton />;

  if (isUnavailable)
    return (
      <NotFoundErrorScreen
        title={getTrans('applications.unavailable.title')}
        description1={getTrans('applications.unavailable.body1')}
        description2={getTrans('applications.unavailable.body2')}
      />
    );

  if (isError)
    return (
      <NotFoundErrorScreen
        title={getTrans('applications.error.title')}
        description1={getTrans('applications.error.body1')}
        description2={getTrans('applications.error.body2')}
      />
    );

  if (!applications) return null;

  if (!applications.length)
    return (
      <EmptyState
        body1={getTrans('applications.empty.body1')}
        body2={getTrans('applications.empty.body2')}
      />
    );

  return (
    <Box component="section" aria-label={getTrans('applications.block.title')}>
      <ApplicationsBlock applications={applications} now={now} />
    </Box>
  );
};
